import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { F } from '../styles/theme';
import { useTheme, useUI } from '../context/ThemeContext';

// ── Icons ────────────────────────────────────────────────────────────────────
function IconDots({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="5"  cy="12" r="1.8" fill={color} />
      <circle cx="12" cy="12" r="1.8" fill={color} />
      <circle cx="19" cy="12" r="1.8" fill={color} />
    </svg>
  );
}
function IconClose({ size = 20, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2.2} strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
function IconLink({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
    </svg>
  );
}
function IconSort({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M2 4h12M2 8h8M2 12h5" stroke={color} strokeWidth={1.8} strokeLinecap="round"/>
      <path d="M13 9l2 2-2 2" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IconTrash({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}
function IconX({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2.2} strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
function IconCheck({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6.5 9.2 17.3 4 12.1" />
    </svg>
  );
}
function IconReset({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1015.5-6.3" />
      <path d="M19 3v5h-5" />
    </svg>
  );
}

// ── Action-Pill (öffnet zur Seite, Label + Icon) ──────────────────────────────
function ActionPill({ label, onClick, disabled, primary, danger, icon, T, delay = 0 }) {
  const bg = primary
    ? 'linear-gradient(180deg, #C2453F 0%, #B33E3A 100%)' // ruhiges Mattrot, kein Neon
    : T.surface;
  const color = primary ? '#fff' : (danger ? '#B33E3A' : T.text);
  const border = primary
    ? '1px solid rgba(0,0,0,0.06)'
    : `1px solid ${T.line}`;
  // Neutrale Tiefe — kein roter Glow (feedback_no_pills_borders_glow).
  const shadow = primary
    ? '0 1px 2px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.14)'
    : '0 6px 18px rgba(0,0,0,0.35)';

  return (
    <motion.button
      initial={{ opacity: 0, y: 12, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 420, damping: 32, delay }}
      onClick={disabled ? undefined : onClick}
      whileTap={disabled ? undefined : { scale: 0.94 }}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 9,
        padding: '11px 16px', height: 42,
        background: bg,
        backdropFilter: primary ? undefined : 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: primary ? undefined : 'blur(24px) saturate(180%)',
        border,
        borderRadius: 14,
        color,
        fontFamily: F.family, fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        boxShadow: shadow,
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {label}
    </motion.button>
  );
}

/**
 * Kontextabhängiger Floating Action Button für die Transaktionsliste.
 *
 * Props:
 *   mode: 'normal' | 'unmatched' | 'link' | 'reorder'
 *   counterLabel?: string  — kleiner Sticky-Chip über dem FAB (z.B. "1/2 ausgewählt")
 *   actions: Array<{ key, label, icon, onClick, disabled?, primary?, danger? }>
 *   accent?: boolean — roter Pulsing-Dot oben rechts, wenn primäre Aktion verfügbar
 */
export default function TransactionsFab({ counterLabel, actions = [], accent = false }) {
  const { T } = useTheme();
  const ui = useUI();
  const [open, setOpen] = useState(false);

  // Beim Mode-Wechsel automatisch schließen
  useEffect(() => { setOpen(false); }, [actions.length, counterLabel]);

  if (!actions.length) return null;

  return (
    <>
      {/* Scrim hinter dem geöffneten Menü */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 140,
              background: 'rgba(0,0,0,0.42)',
              backdropFilter: 'blur(2px)',
            }}
          />
        )}
      </AnimatePresence>

      {/* Zentrierter App-Container-Wrapper (analog zu BottomNav), damit FAB
          am rechten Rand der 480px-App sitzt, nicht am Viewport-Rand. */}
      <div style={{
        position: 'fixed',
        left: '50%',
        bottom: 0,
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 480,
        height: 0,
        zIndex: 150,
        pointerEvents: 'none',
      }}>
        {/* Sticky Counter-Chip */}
        <AnimatePresence>
          {counterLabel && (
            <motion.div
              key="counter"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ type: 'spring', stiffness: 420, damping: 32 }}
              style={{
                position: 'absolute',
                right: 16,
                bottom: 170,
                padding: '8px 14px',
                borderRadius: 12,
                background: T.surface,
                backdropFilter: 'blur(24px) saturate(180%)',
                WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                border: `1px solid ${T.line}`,
                color: T.text,
                fontSize: 12, fontWeight: 700, fontFamily: F.family,
                letterSpacing: '-0.01em',
                boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                display: 'flex', alignItems: 'center', gap: 7,
                pointerEvents: 'auto',
              }}
            >
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#E4544A' }}/>
              {counterLabel}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action-Stack */}
        <div style={{
          position: 'absolute',
          right: 16,
          bottom: 170 + (counterLabel ? 44 : 0),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 10,
          pointerEvents: open ? 'auto' : 'none',
        }}>
          <AnimatePresence>
            {open && actions.map((a, i) => (
              <ActionPill
                key={a.key}
                label={a.label}
                icon={a.icon}
                onClick={() => { a.onClick(); setOpen(false); }}
                disabled={a.disabled}
                primary={a.primary}
                danger={a.danger}
                T={T}
                delay={i * 0.025}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* FAB selbst */}
        <motion.button
          onClick={() => setOpen(o => !o)}
          whileTap={{ scale: 0.92 }}
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 30 }}
          style={{
            position: 'absolute',
            right: 16,
            bottom: 110,
            width: ui.touch.fab, height: ui.touch.fab,
            borderRadius: '50%',
            background: T.surface,
            backdropFilter: 'blur(28px) saturate(200%)',
            WebkitBackdropFilter: 'blur(28px) saturate(200%)',
            // Neutraler Border + neutrale Tiefe (kein Rot-Tint/Glow). Verfügbare
            // Primäraktion wird allein über den roten Dot signalisiert.
            border: `1px solid ${T.line}`,
            color: T.text,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: ui.shadow.lift,
            padding: 0,
            pointerEvents: 'auto',
          }}
        >
          {open ? <IconClose color={T.text} /> : <IconDots color={T.text} />}
          {accent && !open && (
            <span style={{
              position: 'absolute', top: 8, right: 8,
              width: 9, height: 9, borderRadius: '50%',
              background: '#E4544A',
              border: `1.5px solid ${T.surface}`,
            }}/>
          )}
        </motion.button>
      </div>
    </>
  );
}

// Export icons so the parent can compose actions with consistent visuals
export { IconLink, IconSort, IconTrash, IconX, IconCheck, IconReset };
